import { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { refreshToken } from "@/features/auth/services/auth.service";
import { AuthStorageService } from "@/features/auth/services/storage.service";
import { isAuthLoginResponse } from "@/features/auth/types/auth.type";
import { authSessionBus } from "@/shared/lib/authSessionBus";
import { Fetcher } from "@/shared/lib/fetcher.client";

type QueueItem = {
  resolve: (value: Promise<AxiosResponse<unknown>>) => void
  reject: (error: Error) => void
  config: InternalAxiosRequestConfig
};

export class TokenRefreshManager {
  private authStorageService: AuthStorageService;
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];
  private fetcher: Fetcher;

  constructor(fetcher: Fetcher, authStorageService: AuthStorageService) {
    this.authStorageService = authStorageService;
    this.fetcher = fetcher;
  }

  private resetFailedQueue(): void {
    this.failedQueue = [];
  }

  private addToFailedQueue(promise: QueueItem): void {
    this.failedQueue.push(promise);
  }

  private processFailedQueue(error: Error | null, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      }
      else {
        if (token && config.headers) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        resolve(this.fetcher(config));
      }
    });
    this.resetFailedQueue();
  }

  async handleTokenRefresh(originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }): Promise<AxiosResponse> {
    originalRequest._retry = true;
    const latest = this.authStorageService.getAuthToken();
    const failedHeader = originalRequest.headers.Authorization;
    if (latest && failedHeader && failedHeader !== `Bearer ${latest}`) {
      originalRequest.headers.Authorization = `Bearer ${latest}`;
      return this.fetcher(originalRequest);
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.addToFailedQueue({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    this.isRefreshing = true;

    try {
      const refreshResponse = await refreshToken();

      if (!isAuthLoginResponse(refreshResponse)) {
        throw new Error("Unauthorized");
      }

      const newToken = refreshResponse.access_token;
      this.authStorageService.saveAuthToken(newToken, refreshResponse.expires_in);
      this.processFailedQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return this.fetcher(originalRequest);
    }
    catch (refreshError) {
      const error = refreshError instanceof Error ? refreshError : new Error("Unknown refresh error");
      this.processFailedQueue(error, null);
      this.authStorageService.deleteAuthToken();
      authSessionBus.triggerUnauthorized();

      throw error;
    }
    finally {
      this.isRefreshing = false;
    }
  }
}
